
import * as Sentry from "@sentry/react";

export const initSentry = () => {
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN || '';
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.VITE_APP_ENV || 'development';
  const sentryEnabled = import.meta.env.VITE_ENABLE_SENTRY === 'true';

  if (sentryEnabled && sentryDsn) {
    Sentry.init({
      dsn: sentryDsn,
      environment,
      sendDefaultPii: true,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration()
      ],
      // Tracing
      tracesSampleRate: 1.0, //  Capture 100% of the transactions
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: ["localhost", /^https:\/\/.*\.hbox\.ai/],
      // Session Replay
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
      // Enable logs to be sent to Sentry
      enableLogs: true
    });
  }
};

// Helper function to capture custom errors
export const captureSentryError = (error: Error, context?: Record<string, any>) => {
  Sentry.withScope((scope) => {
    if (context) {
      Object.keys(context).forEach(key => {
        scope.setExtra(key, context[key]);
      });
    }
    Sentry.captureException(error);
  });
};

// Set the logged-in user on Sentry's scope as both `user` context and
// top-level tags, so user_id/user_email/user_name are attached and
// filterable on every event (errors, transactions, and logs) sent afterward.
export const setSentryUserContext = (user: { id: string; email?: string; fullName?: string }) => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.fullName,
  });

  Sentry.setTags({
    user_id: user.id,
    user_email: user.email,
    user_name: user.fullName,
  });
};

// Clear user context and tags on logout so subsequent events aren't
// attributed to the previous user.
export const clearSentryUserContext = () => {
  Sentry.setUser(null);
  Sentry.setTags({
    user_id: undefined,
    user_email: undefined,
    user_name: undefined,
  });
};